import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from "lucide-react";
import * as z from "zod";
import { createMetadataValue, metadataArrayLimits } from "../../../lib/schedule-presets/metadata-schema";
import { MetadataField } from "./MetadataField";
import { useMetadataForm } from "./MetadataFormContext";
import { type MetadataAction } from "./MetadataActions";

export function MetadataArrayEditor({ schema, items, labels, disabled, onChange }: {
  schema: z.ZodArray;
  items: unknown[];
  labels: Record<string, string>;
  disabled?: boolean;
  onChange: (items: unknown[]) => void;
}) {
  const { t } = useTranslation();
  const { values } = useMetadataForm();
  const element = schema.element as z.ZodType;
  const { min, max } = metadataArrayLimits(schema);
  const canAdd = items.length < max;
  const canRemove = items.length > min;
  const move = (index: number, direction: number) => {
    const target = index + direction;
    const next = [...items];
    next[index] = items[target];
    next[target] = items[index];
    onChange(next);
  };

  return (
    <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50 [&>[data-slot=collapsible]]:rounded-none [&>[data-slot=collapsible]]:border-0">
      {items.map((item, index) => {
        let label = t("metadata.item", { number: index + 1 });
        if (typeof item === "object" && item !== null && "bind" in item) {
          const binding = String(item.bind);
          label = `${label} · ${labels[binding] ?? binding}`;
        }
        const actions: MetadataAction[] = [
          { label: t("metadata.moveUp"), icon: ArrowUp, disabled: index === 0, onClick: () => move(index, -1) },
          { label: t("metadata.moveDown"), icon: ArrowDown, disabled: index === items.length - 1, onClick: () => move(index, 1) },
          { label: t("metadata.duplicate"), icon: Copy, disabled: !canAdd, onClick: () => {
            const next = [...items];
            next.splice(index + 1, 0, structuredClone(item));
            onChange(next);
          } },
          { label: t("metadata.remove"), icon: Trash2, disabled: !canRemove, destructive: true,
            onClick: () => onChange(items.filter((_, itemIndex) => itemIndex !== index)) },
        ];
        return (
            <MetadataField key={index} schema={element} value={item} label={label} labels={labels} disabled={disabled}
              actions={actions}
              onChange={next => onChange(items.map((current, itemIndex) => itemIndex === index ? next : current))} />
        );
      })}
      <button type="button" disabled={disabled || !canAdd}
        className="flex h-[34px] w-full items-center gap-2 px-2.5 text-left text-xs text-neutral-600 hover:bg-white hover:text-neutral-900 focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_1.5px_#185fa5] disabled:pointer-events-none disabled:opacity-50"
        onClick={() => onChange([...items, createMetadataValue(element, values)])}>
        <Plus className="size-3.5" />
        {t("metadata.addGlobal")}
      </button>
    </div>
  );
}
